import PaymentFormInfo from 'types/PaymentFormInfo';

/**
 * Per-tab persistence for the hosted payment form returned by finalize.
 *
 * Why: the pay form URL only comes back from the finalize call and used to
 * live only in React state. Any full document load on /pay (reload, mobile
 * tab discard, back/forward) lost it, and the Orders service reports these
 * orders as "Invoiced" (not "Finalized") on a fresh fetch, so users were
 * bounced to Billing. A stored form is proof finalize ran in this tab, which
 * lets Base allow the Payment page for an Invoiced order.
 *
 * sessionStorage is used on purpose: it is scoped to the tab (a hosted
 * transaction is single-use, so it must not leak into a second tab) and it
 * survives reloads, back/forward restores and mobile tab discard.
 */

/**
 * ProPay hosted transactions are swept by the Orders service once they are
 * about an hour old. Stop trusting a stored form well before that.
 */
export const PAYMENT_FORM_MAX_AGE_MS = 45 * 60 * 1000;

export interface StoredPaymentForm {
  orderId: number;
  paymentType: string;
  paymentFormInfo: PaymentFormInfo;
  savedAt: number;
}

export type StoredPaymentFormLookup =
  | { status: 'valid'; record: StoredPaymentForm; ageSeconds: number }
  | { status: 'none' | 'stale' | 'order_mismatch' | 'invalid' };

const fallbackMemoryMap = new Map<string, StoredPaymentForm>();

const storageKey = (invoiceId: string, environment: string): string =>
  `${invoiceId}_${environment}_paymentForm`;

const currentEnvironment = (): string => process.env.NODE_ENV || 'development';

function sessionStore(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const probe = '__payment_form_probe__';
    window.sessionStorage.setItem(probe, probe);
    window.sessionStorage.removeItem(probe);
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
}

function readRaw(key: string): StoredPaymentForm | null {
  const store = sessionStore();
  if (!store) return fallbackMemoryMap.get(key) ?? null;
  try {
    const raw = store.getItem(key);
    return raw ? (JSON.parse(raw) as StoredPaymentForm) : null;
  } catch (e) {
    return null;
  }
}

function isWellFormed(record: StoredPaymentForm | null): record is StoredPaymentForm {
  return (
    !!record &&
    typeof record.savedAt === 'number' &&
    typeof record.orderId === 'number' &&
    typeof record.paymentType === 'string' &&
    !!record.paymentFormInfo?.paymentFormUrl
  );
}

export function clearStoredPaymentForm(invoiceId: string): void {
  const key = storageKey(invoiceId, currentEnvironment());
  fallbackMemoryMap.delete(key);
  const store = sessionStore();
  if (!store) return;
  try {
    store.removeItem(key);
  } catch (e) {
    // ignore
  }
}

export function putStoredPaymentForm(
  invoiceId: string,
  record: Omit<StoredPaymentForm, 'savedAt'>,
  now: number = Date.now()
): void {
  if (!record.paymentFormInfo?.paymentFormUrl) {
    // Nothing usable to restore later; make sure an older form is not reused.
    clearStoredPaymentForm(invoiceId);
    return;
  }
  const key = storageKey(invoiceId, currentEnvironment());
  const value: StoredPaymentForm = { ...record, savedAt: now };
  const store = sessionStore();
  if (!store) {
    fallbackMemoryMap.set(key, value);
    return;
  }
  try {
    store.setItem(key, JSON.stringify(value));
  } catch (e) {
    fallbackMemoryMap.set(key, value);
  }
}

/**
 * Look up the stored form for this invoice. Pass `orderId` once the invoice is
 * loaded so a form from a different order is never reused.
 *
 * Read-only on purpose: it runs on every render, and deleting a stale entry
 * here would make later lookups (and the redirect telemetry) report "none"
 * instead of "stale". Entries are replaced by the next finalize and removed
 * when a card is submitted or the flow reaches Confirmation.
 */
export function getStoredPaymentForm(
  invoiceId: string,
  orderId?: number,
  now: number = Date.now()
): StoredPaymentFormLookup {
  const record = readRaw(storageKey(invoiceId, currentEnvironment()));
  if (!record) return { status: 'none' };
  if (!isWellFormed(record)) return { status: 'invalid' };
  const ageMs = now - record.savedAt;
  if (ageMs < 0 || ageMs > PAYMENT_FORM_MAX_AGE_MS) {
    return { status: 'stale' };
  }
  if (orderId !== undefined && record.orderId !== orderId) {
    return { status: 'order_mismatch' };
  }
  return { status: 'valid', record, ageSeconds: Math.round(ageMs / 1000) };
}

/** The payment type chosen when the stored form was saved, if still usable. */
export function getStoredPaymentType(invoiceId: string): string | undefined {
  const lookup = getStoredPaymentForm(invoiceId);
  return lookup.status === 'valid' ? lookup.record.paymentType : undefined;
}

/** Test-only: reset the in-memory fallback. */
export function resetPaymentFormStorageForTests(): void {
  fallbackMemoryMap.clear();
}
